import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart3, Loader2, Download, Clock, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestServiceReports() {
  const auth = useAuth();
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/request/reports`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReports(res.data);
      } catch (err) {
        toast.error("Failed to load reports");
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/request/export/requests`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "service_requests_export.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">Reports</h1>
          <p className="text-slate-500 mt-1">Service request analytics and insights</p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Department */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Requests by Department
          </h2>
          {reports?.by_department && reports.by_department.length > 0 ? (
            <div className="space-y-3">
              {reports.by_department.map((dept) => (
                <div key={dept._id} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{dept._id}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-slate-100 rounded-full h-2">
                      <div
                        className="bg-teal-500 rounded-full h-2 transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            (dept.count /
                              Math.max(
                                ...reports.by_department.map((d) => d.count)
                              )) *
                              100
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 w-8 text-right">
                      {dept.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No data available</p>
          )}
        </div>

        {/* Average Completion Time */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Avg Completion Time (hours)
          </h2>
          {reports?.avg_completion_time && reports.avg_completion_time.length > 0 ? (
            <div className="space-y-3">
              {reports.avg_completion_time.map((dept) => (
                <div key={dept._id} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{dept._id}</span>
                  <span className="text-sm font-semibold text-slate-700">
                    {dept.avg_hours ? Math.round(dept.avg_hours) : 0}h
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No completion data yet</p>
          )}
        </div>
      </div>

      {/* Summary by Department - Full Table */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mt-6">
        <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-slate-800 mb-4">
          Detailed Summary
        </h2>
        {reports?.by_department && reports.by_department.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="pb-3 font-semibold text-slate-600">Department</th>
                  <th className="pb-3 font-semibold text-slate-600">Requests</th>
                  <th className="pb-3 font-semibold text-slate-600">Avg Time (h)</th>
                </tr>
              </thead>
              <tbody>
                {reports.by_department.map((dept) => {
                  const avgTime = reports.avg_completion_time?.find(
                    (a) => a._id === dept._id
                  );
                  return (
                    <tr key={dept._id} className="border-b border-slate-100">
                      <td className="py-3 font-medium text-slate-800">{dept._id}</td>
                      <td className="py-3 text-slate-600">{dept.count}</td>
                      <td className="py-3 text-slate-600">
                        {avgTime ? Math.round(avgTime.avg_hours) : 0}h
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-slate-500 text-sm">No data available</p>
        )}
      </div>
    </div>
  );
}
